import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Card, Grid, makeStyles, Typography } from "@material-ui/core";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import SaveIcon from "@material-ui/icons/Save";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { HealthDeparmentNumber } from "../../constants/AccountingNumbers";
import { TestResult } from "../../graphql/types";
import CompanyFilter from "../Filters/CompanyFilter";
import DateFilter from "../Filters/DateFilter";
import HealtDeparmentNumberFilter from "../Filters/HealtDeparmentNumberFilter";
import LocationFilter from "../Filters/LocationFilter";
import ResultFilter from "../Filters/ResultFilter";
import UserFilter from "../Filters/UserFilter";

const validationSchema = Yup.object({
  companyId: Yup.string().nullable(),
  locationId: Yup.string().nullable(),
  userId: Yup.string().nullable(),
  result: Yup.mixed<TestResult>()
    .nullable()
    .oneOf(["positiv", "negativ", "invalid", null, undefined]),
  healthDeparmentNumber: Yup.mixed<HealthDeparmentNumber>()
    .nullable()
    .oneOf(["374001539", "374001505", null, undefined]),
  startDate: Yup.string()
    .test("is-valid", "Kein valides Datum. Format: TT.MM.JJJJ", (value) =>
      moment(value).isValid()
    )
    .nullable()
    .required("Startdatum wird benötigt"),
  endDate: Yup.string()
    .test("is-valid", "Kein valides Datum. Format: TT.MM.JJJJ", (value) => {
      return moment(value).isValid();
    })
    .nullable()
    .required("Enddatum wird benötigt"),
});

export interface FilterFormValues {
  companyId?: string | null;
  locationId?: string | null;
  userId?: string | null;
  startDate: MaterialUiPickersDate;
  endDate: MaterialUiPickersDate;
  result?: TestResult | null;
  healthDeparmentNumber?: string | null;
}

const initialValues: FilterFormValues = {
  companyId: null,
  locationId: null,
  userId: null,
  result: null,
  startDate: null,
  endDate: null,
  healthDeparmentNumber: null,
};

const useStyles = makeStyles((theme) => ({
  filter: { marginTop: 40, marginBottom: 20 },
  button: {
    margin: theme.spacing(1),
  },
  cards: { margin: "10px 20px" },
}));

export type FilterFormFunction = (values: FilterFormValues) => Promise<any>;
interface Props {
  onSubmit: FilterFormFunction;
}
const FilterForm: React.FC<Props> = ({ onSubmit }) => {
  const classes = useStyles();

  const methods = useForm<FilterFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    mode: "all",
  });
  const { control, handleSubmit, setValue, watch, formState, reset } = methods;

  const companyId = watch("companyId");
  const healthDeparmentNumber = watch("healthDeparmentNumber");

  useEffect(() => {
    if (healthDeparmentNumber) {
      setValue("locationId", undefined);
    }
  }, [healthDeparmentNumber]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container className={classes.filter} direction="row" spacing={4}>
        <Grid
          item
          xs={12}
          md={6}
          container
          direction="row"
          spacing={3}
          className={classes.cards}
          component={Card}
        >
          <Grid item>
            <Typography variant={"h5"}>Attribut Filter</Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={12}
            container
            direction="row"
            spacing={1}
            justify="space-between"
          >
            <Grid item>
              <Controller
                name="companyId"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CompanyFilter
                    onChange={onChange}
                    onClear={onChange}
                    value={value}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Controller
                name="locationId"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <LocationFilter
                    selectedCompany={companyId}
                    onChange={(locationId, companyId) => {
                      onChange(locationId);
                      setValue("companyId", companyId);
                    }}
                    onClear={() => onChange(null)}
                    value={value}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Controller
                name="userId"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <UserFilter
                    onChange={onChange}
                    onClear={onChange}
                    value={value}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Controller
                name="result"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ResultFilter
                    onChange={onChange}
                    onClear={onChange}
                    value={value}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Controller
                name="healthDeparmentNumber"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <HealtDeparmentNumberFilter
                    onChange={onChange}
                    onClear={onChange}
                    value={value}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          md={5}
          container
          direction="row"
          spacing={3}
          className={classes.cards}
          component={Card}
        >
          <Grid item>
            <Typography variant={"h5"}>Datum</Typography>
          </Grid>
          <Grid item xs={12} md={12} container direction="row" spacing={1}>
            <Grid item>
              <Controller
                name="startDate"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <DateFilter
                    label={"Von"}
                    tooltipTitle="von 00:00:00 Uhr"
                    onChange={onChange}
                    onClear={onChange}
                    selection={value}
                    error={error}
                  />
                )}
              />
            </Grid>
            <Grid item>
              {" "}
              <Controller
                name="endDate"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <DateFilter
                    label={"Bis"}
                    tooltipTitle="bis 23:59:59 Uhr"
                    onChange={onChange}
                    onClear={onChange}
                    selection={value}
                    maxDate={new Date()}
                    error={error}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        xs={12}
        md={12}
        container
        direction="row"
        alignItems="center"
        justify="flex-end"
      >
        <Button
          variant="contained"
          color="primary"
          size="large"
          className={classes.button}
          onClick={handleSubmit(onSubmit)}
          startIcon={<SaveIcon />}
        >
          Graph erstellen
        </Button>

        {formState.isSubmitted && (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => reset(initialValues)}
            size="large"
            className={classes.button}
            startIcon={<RotateLeftIcon />}
          >
            Zurücksetzten
          </Button>
        )}
      </Grid>
    </form>
  );
};

export default FilterForm;
